import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { getEnvironment } from "@/shared/hooks/useGetEnvironment";
import {
  storeOnboardingData,
  promoteAdminIfElayne,
  setAcquisitionSource,
} from "@/shared/utils/onboarding";
import { normalizeName, extractLastName } from "@/shared/utils/names";
import { ONBOARDING_STORAGE_KEYS } from "@/shared/constants/constants";
import { uploadObituaryImageToS3 } from "@/shared/lib/obituaryImageUpload";
import { EventType, trackEvent } from "@/shared/utils/core";
import {
  toErrorType,
  trackUserSignedUp,
  type OnboardingFlow,
} from "@/shared/utils/funnelAnalytics";
import type { Database } from "@/shared/types/database.types";

type UserRow = Database["public"]["Tables"]["users"]["Row"];
type EstateRow = Database["public"]["Tables"]["estates"]["Row"];

interface SignupCredentials {
  email: string;
  password: string;
}

interface UserInfo {
  fullName: string;
  phoneNumber: string;
  obituaryLink?: string;
  helpTopics?: string[];
}

interface PersonInfo {
  fullName: string;
  middleName?: string;
  nickname?: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  lastResidence?: string;
  relationship?: string;
  obituaryLink?: string;
  funeralHome?: string;
  mainImageUrl?: string;
}

interface SendVerificationOptions {
  skipVerification?: boolean;
  isResend?: boolean;
}

interface UseOnboardingAuthOptions {
  analyticsFlow?: OnboardingFlow;
}

interface UseOnboardingAuthReturn {
  isLoading: boolean;
  error: string | null;
  pendingEmail: string | null;
  sendVerificationCode: (
    credentials: SignupCredentials,
    userInfo?: UserInfo,
    options?: SendVerificationOptions,
  ) => Promise<boolean>;
  verifyOtpAndCreateUser: (code: string, userInfo: UserInfo) => Promise<UserRow | null>;
  resendVerificationCode: () => Promise<boolean>;
  updateEmailAndResend: (newEmail: string) => Promise<boolean>;
  createPersonRecord: (personInfo: PersonInfo) => Promise<EstateRow | null>;
  clearError: () => void;
}

const ONBOARDING_EMAIL_KEY = ONBOARDING_STORAGE_KEYS.PENDING_EMAIL;
const ONBOARDING_PASSWORD_KEY = ONBOARDING_STORAGE_KEYS.PENDING_PASSWORD;

export function useOnboardingAuth({
  analyticsFlow = "self_serve",
}: UseOnboardingAuthOptions = {}): UseOnboardingAuthReturn {
  const queryClient = useQueryClient();
  const analyticsFlowRef = useRef(analyticsFlow);
  analyticsFlowRef.current = analyticsFlow;

  const trackSignupFailed = useCallback((err: unknown) => {
    trackEvent(EventType.SIGNUP_FAILED, {
      flow: analyticsFlowRef.current,
      signup_method: "email",
      error_type: toErrorType(err),
    });
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(() => {
    return localStorage.getItem(ONBOARDING_EMAIL_KEY);
  });

  const lastUserInfoRef = useRef<UserInfo | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const sendVerificationCode = useCallback(
    async (
      credentials: SignupCredentials,
      userInfo?: UserInfo,
      options?: SendVerificationOptions,
    ): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const env = getEnvironment();
        const skipOtp = env === "local" || options?.skipVerification === true;

        if (userInfo) {
          lastUserInfoRef.current = userInfo;
        }

        const resolvedUserInfo = userInfo ?? lastUserInfoRef.current;

        if (skipOtp) {
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: credentials.email,
            password: credentials.password,
          });

          if (signUpError) {
            trackSignupFailed(signUpError);
            setError(signUpError.message);
            return false;
          }

          if (!signUpData.user) {
            trackSignupFailed(null);
            setError("Account creation failed. Please try again.");
            return false;
          }

          const normalizedFullName = normalizeName(resolvedUserInfo?.fullName ?? "");
          const nameParts = normalizedFullName.split(/\s+/);
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";

          const { data: userRecord, error: userError } = await supabase
            .from("users")
            .insert({
              auth_id: signUpData.user.id,
              email: credentials.email.toLowerCase().trim(),
              first_name: firstName,
              last_name: lastName,
              full_name: normalizedFullName,
              phone_number: resolvedUserInfo?.phoneNumber ?? "",
              completed_onboarding: false,
              completed_authorization: false,
              contact_by_email: true,
              contact_by_phone: false,
              help_topics: resolvedUserInfo?.helpTopics ?? null,
            })
            .select()
            .single();

          if (userError) {
            trackSignupFailed(userError);
            console.error("Failed to create user record:", userError);
            setError("Failed to create account. Please try again.");
            return false;
          }

          trackUserSignedUp(analyticsFlowRef.current, "email");

          storeOnboardingData({
            userInfo: {
              email: credentials.email.toLowerCase().trim(),
              firstName,
              lastName,
              phoneNumber: resolvedUserInfo?.phoneNumber ?? "",
            },
          });

          if (resolvedUserInfo?.phoneNumber) {
            supabase.functions
              .invoke("communications/phone/validate", {
                body: { userId: userRecord.user_id, phoneNumber: resolvedUserInfo.phoneNumber },
              })
              .catch((err) => console.warn("Phone validation failed (non-blocking):", err));
          }

          promoteAdminIfElayne(userRecord.user_id, credentials.email).catch((err) =>
            console.warn("Admin promotion failed (non-blocking):", err),
          );

          setAcquisitionSource(credentials.email.toLowerCase().trim(), "nok").catch((err) =>
            console.warn("Acquisition source tracking failed (non-blocking):", err),
          );

          localStorage.removeItem(ONBOARDING_PASSWORD_KEY);
          localStorage.removeItem(ONBOARDING_EMAIL_KEY);
          setPendingEmail(null);

          return true;
        }

        const { data, error: invokeError } = await supabase.functions.invoke(
          "communications/verification-code/send",
          { body: { email: credentials.email, password: credentials.password } },
        );

        if (invokeError) {
          trackSignupFailed(invokeError);
          setError("Failed to send verification code. Please try again.");
          return false;
        }

        if (data?.error) {
          trackSignupFailed(data.error);
          setError(data.error);
          return false;
        }

        trackEvent(EventType.VERIFICATION_CODE_SENT, {
          flow: analyticsFlowRef.current,
          is_resend: options?.isResend === true,
        });

        localStorage.setItem(ONBOARDING_EMAIL_KEY, credentials.email);
        localStorage.setItem(ONBOARDING_PASSWORD_KEY, credentials.password);
        setPendingEmail(credentials.email);

        return true;
      } catch (err) {
        trackSignupFailed(err);
        console.error("sendVerificationCode error:", err);
        setError("An unexpected error occurred. Please try again.");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [trackSignupFailed],
  );

  const verifyOtpAndCreateUser = useCallback(
    async (code: string, userInfo: UserInfo): Promise<UserRow | null> => {
      setIsLoading(true);
      setError(null);

      const email = localStorage.getItem(ONBOARDING_EMAIL_KEY);

      if (!email) {
        setError("Session expired. Please start over.");
        setIsLoading(false);
        return null;
      }

      try {
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          email,
          token: code,
          type: "signup",
        });

        if (verifyError) {
          const errorType = toErrorType(verifyError);
          trackEvent(EventType.VERIFICATION_CODE_FAILED, {
            flow: analyticsFlowRef.current,
            error_type: errorType === "unknown" ? "invalid_code" : errorType,
          });
          setError("Invalid code");
          return null;
        }

        if (!verifyData.user) {
          setError("Verification failed. Please try again.");
          return null;
        }

        const normalizedFullName = normalizeName(userInfo.fullName);
        const nameParts = normalizedFullName.split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const { data: userRecord, error: userError } = await supabase
          .from("users")
          .insert({
            auth_id: verifyData.user.id,
            email: email.toLowerCase().trim(),
            first_name: firstName,
            last_name: lastName,
            full_name: normalizedFullName,
            phone_number: userInfo.phoneNumber,
            completed_onboarding: false,
            completed_authorization: false,
            contact_by_email: true,
            contact_by_phone: false,
            help_topics: userInfo.helpTopics ?? null,
          })
          .select()
          .single();

        if (userError) {
          trackSignupFailed(userError);
          console.error("Failed to create user record:", userError);
          setError("Failed to create account. Please try again.");
          return null;
        }

        trackUserSignedUp(analyticsFlowRef.current, "email");

        storeOnboardingData({
          userInfo: {
            email: email.toLowerCase().trim(),
            firstName,
            lastName,
            phoneNumber: userInfo.phoneNumber,
          },
        });

        supabase.functions
          .invoke("account/confirm-email", {
            body: { email: email.toLowerCase().trim() },
          })
          .catch((err) => console.warn("Email confirmation failed (non-blocking):", err));

        promoteAdminIfElayne(userRecord.user_id, email).catch((err) =>
          console.warn("Admin promotion failed (non-blocking):", err),
        );

        setAcquisitionSource(email.toLowerCase().trim(), "nok").catch((err) =>
          console.warn("Acquisition source tracking failed (non-blocking):", err),
        );

        localStorage.removeItem(ONBOARDING_PASSWORD_KEY);
        localStorage.removeItem(ONBOARDING_EMAIL_KEY);
        setPendingEmail(null);

        return userRecord;
      } catch (err) {
        trackSignupFailed(err);
        console.error("verifyOtpAndCreateUser error:", err);
        setError("An unexpected error occurred. Please try again.");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [trackSignupFailed],
  );

  const resendVerificationCode = useCallback(async (): Promise<boolean> => {
    const email = localStorage.getItem(ONBOARDING_EMAIL_KEY);
    const password = localStorage.getItem(ONBOARDING_PASSWORD_KEY);

    if (!email || !password) {
      setError("Session expired. Please start over.");
      return false;
    }

    return sendVerificationCode({ email, password }, undefined, { isResend: true });
  }, [sendVerificationCode]);

  const updateEmailAndResend = useCallback(
    async (newEmail: string): Promise<boolean> => {
      const password = localStorage.getItem(ONBOARDING_PASSWORD_KEY);

      if (!password) {
        setError("Session expired. Please start over.");
        return false;
      }

      return sendVerificationCode({ email: newEmail, password }, undefined, { isResend: true });
    },
    [sendVerificationCode],
  );

  const createPersonRecord = useCallback(
    async (personInfo: PersonInfo): Promise<EstateRow | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();

        if (!authUser) {
          setError("Not authenticated. Please sign in again.");
          return null;
        }

        const { data: userRecord, error: userQueryError } = await supabase
          .from("users")
          .select("user_id")
          .eq("auth_id", authUser.id)
          .single();

        if (userQueryError || !userRecord) {
          console.error("Failed to find user record:", userQueryError);
          setError("Account not found. Please try again.");
          return null;
        }

        const normalizedPersonName = normalizeName(personInfo.fullName);
        const nameParts = normalizedPersonName.split(/\s+/);
        const firstName = nameParts[0] || "";
        const middleName = personInfo.middleName ? normalizeName(personInfo.middleName) : null;
        const lastName = extractLastName(nameParts, middleName ?? "");

        const personData = {
          full_name: normalizedPersonName,
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          date_of_birth: personInfo.dateOfBirth || null,
          date_of_death: personInfo.dateOfDeath || null,
          location: personInfo.lastResidence || null,
          funeral_home: personInfo.funeralHome || null,
          nickname: personInfo.nickname || null,
          obituary_link: personInfo.obituaryLink || null,
        };

        // Check if person already exists for this user — update instead of creating duplicate
        const { data: existingRelation } = await supabase
          .from("user_estates")
          .select("estate_id")
          .eq("user_id", userRecord.user_id)
          .limit(1)
          .single();

        let personRecord: EstateRow;

        if (existingRelation) {
          const { error: updateError } = await supabase
            .from("estates")
            .update(personData)
            .eq("estate_id", existingRelation.estate_id);

          if (updateError) {
            console.error("Failed to update person record:", updateError);
            setError("Failed to save information. Please try again.");
            return null;
          }

          if (personInfo.mainImageUrl) {
            const s3Key = await uploadObituaryImageToS3(
              personInfo.mainImageUrl,
              existingRelation.estate_id,
            );
            if (s3Key) {
              await supabase
                .from("estates")
                .update({ main_image_url: s3Key })
                .eq("estate_id", existingRelation.estate_id);
            }
          }

          const { data: updated } = await supabase
            .from("estates")
            .select("*")
            .eq("estate_id", existingRelation.estate_id)
            .single();

          if (!updated) {
            setError("Failed to save information. Please try again.");
            return null;
          }

          personRecord = updated;
        } else {
          const { data: newPerson, error: personError } = await supabase
            .from("estates")
            .insert({
              ...personData,
              owner_user_id: userRecord.user_id,
              main_image_url: null,
              estate_type: "deceased" as const,
            })
            .select()
            .single();

          if (personError || !newPerson) {
            console.error("Failed to create person record:", personError);
            setError("Failed to save information. Please try again.");
            return null;
          }

          if (personInfo.mainImageUrl) {
            const s3Key = await uploadObituaryImageToS3(
              personInfo.mainImageUrl,
              newPerson.estate_id,
            );
            if (s3Key) {
              await supabase
                .from("estates")
                .update({ main_image_url: s3Key })
                .eq("estate_id", newPerson.estate_id);
            }
          }

          const { error: relationError } = await supabase.from("user_estates").insert({
            user_id: userRecord.user_id,
            estate_id: newPerson.estate_id,
          });

          if (relationError) {
            console.error("Failed to create user-person relationship:", relationError);
          }

          await seedEstateTasks(newPerson.estate_id);
          personRecord = newPerson;
        }

        await supabase
          .from("users")
          .update({ last_active_estate_id: personRecord.estate_id })
          .eq("user_id", userRecord.user_id);

        storeOnboardingData({
          personInfo: {
            fullName: personInfo.fullName,
            dateOfBirth: personInfo.dateOfBirth,
            dateOfDeath: personInfo.dateOfDeath,
            lastResidence: personInfo.lastResidence,
            funeralHome: personInfo.funeralHome,
            mainImageUrl: personInfo.mainImageUrl,
            obituaryLink: personInfo.obituaryLink,
            personType: "deceased",
          },
        });

        if (personInfo.mainImageUrl) {
          queryClient.setQueryData(
            ["person-photo-url", personRecord.estate_id, undefined],
            personInfo.mainImageUrl,
          );
        }

        await queryClient.invalidateQueries({ queryKey: ["app-user-data"], exact: false });

        return personRecord;
      } catch (err) {
        console.error("createPersonRecord error:", err);
        setError("An unexpected error occurred. Please try again.");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient],
  );

  return {
    isLoading,
    error,
    pendingEmail,
    sendVerificationCode,
    verifyOtpAndCreateUser,
    resendVerificationCode,
    updateEmailAndResend,
    createPersonRecord,
    clearError,
  };
}

async function seedEstateTasks(estateId: number): Promise<void> {
  try {
    const { data: seeds, error: seedError } = await supabase.from("seed_tasks").select("task_id");

    if (seedError || !seeds || seeds.length === 0) {
      console.warn("No seed tasks found to assign to person");
      return;
    }

    const rows = seeds.map((s) => ({
      estate_id: estateId,
      task_id: s.task_id,
      status: "not_started",
    }));

    const { error: insertError } = await supabase.from("estate_tasks").insert(rows);

    if (insertError) {
      console.error("Failed to create estate tasks:", insertError);
    }
  } catch (err) {
    console.error("Error seeding estate tasks:", err);
  }
}
